import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
    Palette, Smartphone, RefreshCw, Layers, Church, Briefcase, User,
    CalendarDays, Mail, Globe, Search, ShieldCheck, Repeat, Sparkles, ArrowUpRight,
} from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";

const SERVICES = [
    { icon: Palette, title: "Custom Website Design", desc: "A bespoke site designed around your brand — never a template." },
    { icon: Smartphone, title: "Mobile Optimization", desc: "Flawless on every phone, tablet, and screen size." },
    { icon: RefreshCw, title: "Website Redesigns", desc: "Turn an outdated site into a modern, high-converting experience." },
    { icon: Layers, title: "Landing Pages", desc: "Focused pages built to capture attention and drive action." },
    { icon: Church, title: "Ministry & Church Websites", desc: "Reach your congregation and community with excellence." },
    { icon: Briefcase, title: "Business Websites", desc: "A professional home base that builds trust and wins clients." },
    { icon: User, title: "Personal Brand Websites", desc: "Put your name, story, and expertise on center stage." },
    { icon: CalendarDays, title: "Event Websites", desc: "Promote, inform, and fill seats for your next event." },
    { icon: Mail, title: "Contact & Inquiry Forms", desc: "Make it effortless for people to reach you." },
    { icon: Globe, title: "Domain Setup Assistance", desc: "Your name, secured and connected the right way." },
    { icon: Search, title: "Basic SEO Setup", desc: "Get found on Google with the fundamentals done right." },
    { icon: ShieldCheck, title: "Website Maintenance", desc: "Updates, backups, and peace of mind — handled." },
    { icon: Repeat, title: "Website Updates", desc: "Fresh content and changes whenever you need them." },
    { icon: Sparkles, title: "Digital Branding Support", desc: "A cohesive look and voice across your digital presence." },
];

const IconBadge = ({ icon: Icon, index, total, progress, reduce }) => {
    const t0 = (index / total) * 0.72;
    const t1 = Math.min(t0 + 0.24, 1);
    const draw = useTransform(progress, [t0, t1], [0, 1]);
    const iconScale = useTransform(progress, [t0, t1], [0.5, 1]);
    const iconOpacity = useTransform(progress, [t0, t1], [0, 1]);

    return (
        <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl glass">
            <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <circle cx="24" cy="24" r="21" stroke="rgba(10,15,29,0.1)" strokeWidth="1.5" />
                <motion.circle
                    cx="24"
                    cy="24"
                    r="21"
                    stroke="#38BDF8"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    style={{ pathLength: reduce ? 1 : draw }}
                />
            </svg>
            <motion.span
                style={reduce ? {} : { scale: iconScale, opacity: iconOpacity }}
                className="text-[#0066FF] transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110"
            >
                <Icon className="h-5 w-5" />
            </motion.span>
        </span>
    );
};

const ServiceCard = ({ s, i, total, progress, reduce }) => (
    <motion.article
        data-testid={`service-card-${i}`}
        className="group relative overflow-hidden rounded-2xl border border-[#0A0F1D]/10 bg-white p-6 transition-[border-color,background-color,transform] duration-500 hover:-translate-y-1.5 hover:border-[#0066FF]/50 hover:bg-[#F0F5FF]"
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
    >
        <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#0066FF]/0 blur-2xl transition-[background-color] duration-500 group-hover:bg-[#0066FF]/20" aria-hidden="true" />
        <div className="relative flex items-start justify-between">
            <IconBadge icon={s.icon} index={i} total={total} progress={progress} reduce={reduce} />
            <ArrowUpRight className="h-4 w-4 text-slate-600 transition-[color,transform] duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#0066FF]" />
        </div>
        <h3 className="relative mt-5 font-display text-base font-bold text-[#0A0F1D] sm:text-lg">{s.title}</h3>
        <p className="relative mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
    </motion.article>
);

export const Services = () => {
    const reduce = useReducedMotion();
    const gridRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: gridRef,
        offset: ["start 0.95", "end 0.55"],
    });

    return (
        <section id="services" className="relative py-24 md:py-36" data-testid="services-section">
            <div className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-[#0066FF]/40 to-transparent" aria-hidden="true" />
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <Reveal>
                            <Eyebrow>Services</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-[#0A0F1D] sm:text-5xl lg:text-6xl">
                                MORE THAN<br />A WEBSITE<span className="text-electric-grad">.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}>
                        <p className="max-w-sm text-sm leading-relaxed text-slate-600 sm:text-base">
                            Everything you need to launch — and everything you need after. One partner for your entire digital presence.
                        </p>
                    </Reveal>
                </div>

                <div ref={gridRef} className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {SERVICES.map((s, i) => (
                        <ServiceCard
                            key={s.title}
                            s={s}
                            i={i}
                            total={SERVICES.length}
                            progress={scrollYProgress}
                            reduce={reduce}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};
